import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { valueKind, type ResolvedPath } from "../definition";
import { choiceLabel, classroomOptionLabel } from "../labels";
import type { Catalog, ConditionValue } from "../types";
import { input } from "./styles";

type Props = {
  catalog: Catalog;
  resolved: ResolvedPath;
  operator: string;
  schoolYearId: string | null;
  value: ConditionValue | undefined;
  onChange: (value: ConditionValue) => void;
};

/** The value of a condition, in an input that fits the field type. */
export default function ValueInput({ catalog, resolved, operator, schoolYearId, value, onChange }: Props): ReactElement | null {
  const { t } = useTranslation();
  const field = resolved.field;
  const kind = valueKind(operator);
  if (kind === "NONE") return null;
  // A relation itself is compared by its number (e.g. number of children).
  const type = field?.type ?? "NUMBER";

  const htmlType = type === "DATE" ? "date" : type === "NUMBER" ? "number" : "text";
  const label = String(t("reports.builder.value", { defaultValue: "Value" }));

  if (kind === "SINGLE") {
    return (
      <input
        aria-label={label}
        type={htmlType}
        value={value === null || value === undefined || Array.isArray(value) ? "" : String(value)}
        maxLength={200}
        onChange={(event) => onChange(event.target.value)}
        className={input}
      />
    );
  }

  if (kind === "DAYS") {
    return (
      <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
        <input
          aria-label={label}
          type="number"
          min={1}
          max={3650}
          value={value === null || value === undefined || Array.isArray(value) ? "" : String(value)}
          onChange={(event) => onChange(event.target.value === "" ? null : Number(event.target.value))}
          className={`${input} w-28`}
        />
        {t("reports.builder.days_unit", { defaultValue: "days" })}
      </label>
    );
  }

  if (kind === "RANGE") {
    const range = Array.isArray(value) ? value : ["", ""];
    const set = (index: number, next: string) => {
      const copy = [range[0] ?? "", range[1] ?? ""];
      copy[index] = next;
      onChange(copy);
    };
    return (
      <div className="flex items-center gap-2">
        <input aria-label={String(t("reports.builder.from", { defaultValue: "From" }))} type={htmlType}
               value={String(range[0] ?? "")} onChange={(event) => set(0, event.target.value)} className={input} />
        <span className="text-sm text-slate-500">{t("reports.builder.and", { defaultValue: "and" })}</span>
        <input aria-label={String(t("reports.builder.to", { defaultValue: "To" }))} type={htmlType}
               value={String(range[1] ?? "")} onChange={(event) => set(1, event.target.value)} className={input} />
      </div>
    );
  }

  // LIST: choices, or the classes of the report's school year.
  if (!field) return null;
  const selected = new Set((Array.isArray(value) ? value : []).map(String));
  const options = field.type === "CLASSROOM"
    ? catalog.classrooms
      .filter((classroom) => !schoolYearId || classroom.schoolYearId === schoolYearId)
      .map((classroom) => ({ value: classroom.id, label: classroomOptionLabel(t, classroom) }))
    : field.choices.map((choice) => ({ value: choice.value, label: choiceLabel(t, field, choice.value) }));
  const toggle = (option: string) => {
    const next = new Set(selected);
    if (next.has(option)) next.delete(option);
    else next.add(option);
    onChange(Array.from(next));
  };
  if (options.length === 0) {
    return (
      <p className="text-sm text-slate-500 dark:text-slate-400">
        {t("reports.builder.no_options", { defaultValue: "There is nothing to choose from." })}
      </p>
    );
  }
  return (
    <fieldset className="max-h-48 space-y-0.5 overflow-y-auto rounded-2xl border border-slate-200 p-2 dark:border-white/10">
      <legend className="sr-only">{label}</legend>
      {options.map((option) => (
        <label key={option.value}
               className="flex cursor-pointer items-center gap-3 rounded-xl px-2 py-1 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-white/5">
          <input type="checkbox" checked={selected.has(option.value)} onChange={() => toggle(option.value)}
                 className="h-4 w-4 accent-indigo-600" />
          {option.label}
        </label>
      ))}
    </fieldset>
  );
}
